import { useEffect, useState, useRef } from 'react'
import { DocumentArrowDownIcon, FunnelIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import ButtonComponent from 'components/common/ButtonComponent'
import TableComponent from 'components/common/TableComponent'
import PickingDataEditModal from 'components/modals/PickingDataEditModal'
import PageLoadingState from 'components/common/PageLoadingState'
import DateRangeFilterModal from 'components/modals/DateRangeFilterModal'
import { parseDate } from 'utils'
import { makeRequest } from 'services/request.service'
import { showAlert } from 'services/alerts.service'
import { useAuth } from 'auth/AuthContext'
import { DateTime } from 'luxon'
import { useDashboardDateRange } from 'hooks/useDashboardDateRange'
import { useDashboardFilterOptions } from 'hooks/useDashboardFilterOptions'
import { buildDashboardDateRangeQuery, getDateRangeSummary } from 'utils/dateRangeFilters'
import { exportDataEditExcel, exportDataEditPdf, filterRowsBySearch } from 'utils/dataEditExport'

const exportColumns = [
  { key: 'block', label: 'Block' }, { key: 'workerName', label: 'Worker' },
  { key: 'date', label: 'Date' }, { key: 'containerName', label: 'Container' },
  { key: 'totalUnits', label: 'Total Units' }, { key: 'credit', label: 'Credit' }
]

export default function PickingDataEdit () {
  const { user } = useAuth()
  const hasFetched = useRef(false)
  const currentFilter = useRef(null)
  const currentDate = DateTime.now().setZone('America/Mexico_City').toFormat('yyyy-MM-dd')
  const [dateRange, setDateRange] = useDashboardDateRange(currentDate)
  const filterOptions = useDashboardFilterOptions(user?.farmId)
  const [showFilters, setShowFilters] = useState(false)
  const [selectedRows, setSelectedRows] = useState([])
  const [state, setState] = useState({
    pickingData: [],
    searchTerm: '',
    showEditModal: false,
    selectedPickingData: null,
    isBulkEdit: false,
    loading: true
  })

  const getPickingData = async () => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: `/management/data-edit/picking-data/${user.farmId}?${buildDashboardDateRangeQuery(dateRange)}`,
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
      })
      setState(s => ({ ...s, pickingData: response.data, loading: false }))
    } catch (error) {
      console.error('Error:', error)
      setState(s => ({ ...s, loading: false }))
    }
  }

  const openSingleEditModal = (row) => {
    setState(s => ({ ...s, showEditModal: true, selectedPickingData: row, isBulkEdit: false }))
  }

  const openBulkEditModal = () => {
    setState(s => ({ ...s, showEditModal: true, selectedPickingData: null, isBulkEdit: true }))
  }

  const closeModal = () => {
    setState(s => ({ ...s, showEditModal: false, selectedPickingData: null, isBulkEdit: false }))
  }

  const handleSave = async (formData) => {
    try {
      if (state.isBulkEdit) {
        const ids = selectedRows.map(r => r.pickingDataId)
        await makeRequest({
          method: 'PATCH',
          url: '/management/data-edit/picking-data/bulk',
          data: { ids, ...formData },
          headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
        })
      } else {
        await makeRequest({
          method: 'PATCH',
          url: `/management/data-edit/picking-data/${state.selectedPickingData.pickingDataId}`,
          data: formData,
          headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
        })
      }
      showAlert({ text: 'Picking data updated', icon: 'success' })
      closeModal()
      await getPickingData()
    } catch (error) {
      showAlert({ text: 'Failed to update picking data', icon: 'error' })
      console.error('Error updating picking data:', error)
    }
  }

  const handleDelete = async (row) => {
    try {
      await makeRequest({
        method: 'DELETE',
        url: `/management/data-edit/picking-data/${row.pickingDataId}`,
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
      })
      showAlert({ text: 'Picking data deleted', icon: 'success' })
      await getPickingData()
    } catch (error) {
      showAlert({ text: 'Failed to delete picking data', icon: 'error' })
      console.error('Error deleting picking data:', error)
    }
  }

  const filteredData = filterRowsBySearch(state.pickingData, state.searchTerm)

  useEffect(() => {
    if (!user?.farmId) return
    const nextFilter = JSON.stringify(dateRange)
    if (hasFetched.current && currentFilter.current === nextFilter) return
    hasFetched.current = true
    currentFilter.current = nextFilter
    getPickingData()
  }, [user?.farmId, dateRange])

  if (state.loading) {
    return <PageLoadingState message='Loading picking data...' rows={8} />
  }

  return (
    <div className='p-6 bg-white shadow-md border border-gray-300 border-t-0'>
      <div className='section-header'>
        <div className='header-left'>
          <div className='date-filter'>
            <span>{getDateRangeSummary(dateRange)}</span>
          </div>
        </div>

        <div className='flex items-center gap-4 mb-6'>
          {selectedRows.length > 0 && (
            <ButtonComponent className='flex items-center gap-2' onClick={openBulkEditModal}>
              <PencilSquareIcon className='w-5 h-5' />
              <span>Edit selection ({selectedRows.length})</span>
            </ButtonComponent>
          )}
          <ButtonComponent className='flex items-center gap-2' onClick={() => exportDataEditExcel({ rows: filteredData, columns: exportColumns, fileName: `picking-data-${currentDate}`, sheetName: 'Picking Data' })}>
            <DocumentArrowDownIcon className='w-5 h-5' />
            <span>Export as Excel</span>
          </ButtonComponent>
          <ButtonComponent className='flex items-center gap-2' onClick={() => exportDataEditPdf({ rows: filteredData, columns: exportColumns, fileName: `picking-data-${currentDate}`, title: 'Picking Data' })}>
            <DocumentArrowDownIcon className='w-5 h-5' />
            <span>Export as PDF</span>
          </ButtonComponent>
        </div>
      </div>

      <div className='search-container flex gap-4 mb-4'>
        <input
          type='text'
          placeholder='Search all fields...'
          value={state.searchTerm}
          onChange={(e) => setState(s => ({ ...s, searchTerm: e.target.value.toLowerCase() }))}
          className='w-48 mt-1 p-2 border rounded-md shadow-sm'
          data-align='left'
        />
        <ButtonComponent className='flex items-center gap-2' onClick={() => setShowFilters(true)}><FunnelIcon className='w-5 h-5' /><span>Advanced filters</span></ButtonComponent>
      </div>

      <div className='data-table'>
        {state.pickingData.length !== 0 && (
          <TableComponent
            tableData={{
              hasCheckbox: true,
              columns: [
                { key: 'block', label: 'Block' },
                { key: 'workerName', label: 'Worker' },
                { key: 'date', label: 'Date', cell: (value) => parseDate(value) },
                { key: 'containerName', label: 'Container' },
                { key: 'totalUnits', label: 'Total Units' },
                { key: 'credit', label: 'Credit' },
                { key: 'actions', label: 'Manage' }
              ],
              rows: filteredData
            }}
            handleEdit={openSingleEditModal}
            handleDelete={handleDelete}
            onSelectionChange={setSelectedRows}
            defaultSortColumn='date'
          />
        )}
      </div>

      <PickingDataEditModal
        isOpen={state.showEditModal}
        handleClose={closeModal}
        handleSave={handleSave}
        selectedPickingData={state.selectedPickingData}
        isBulk={state.isBulkEdit}
        bulkCount={selectedRows.length}
        farmId={user.farmId}
      />
      <DateRangeFilterModal isOpen={showFilters} dateRange={dateRange} sectors={filterOptions.sectors} farmingBlocks={filterOptions.farmingBlocks} groups={filterOptions.groups} activities={filterOptions.activities} maxDate={currentDate} onClose={() => setShowFilters(false)} onApply={(nextRange) => { setDateRange(nextRange); setShowFilters(false) }} />
    </div>
  )
}
